/**
 * Internal dependencies
 */
import type { ValidationErrors } from "../../types/add-to-cart.types";
import { ProductVariant } from "../api/product-api.service";

/** Mutable accumulator used only while building a ValidationErrors result */
type MutableValidationErrors = {
	-readonly [K in keyof ValidationErrors]: ValidationErrors[K];
};

/**
 * Validation rules for add-to-cart form
 */
export const AddToCartValidator = {
	/**
	 * Validate quantity
	 */
	validateQuantity(quantity: number): string | undefined {
		if (quantity < 1) {
			return "Quantity must be at least 1";
		}
		if (quantity > 100) {
			return "Quantity cannot exceed 100";
		}
		if (!Number.isInteger(quantity)) {
			return "Quantity must be a whole number";
		}
		return undefined;
	},

	/**
	 * Validate variant selection
	 */
	validateVariant(
		variantId: null | number,
		variants: readonly ProductVariant[],
		productType?: string,
	): string | undefined {
		if (productType === "simple") {
			return undefined;
		}

		if (!variantId) {
			return "Please select a variant";
		}
		if (
			variants.length > 0 &&
			!variants.some((v: ProductVariant) => v.id === variantId)
		) {
			return "Selected variant is not available";
		}
		return undefined;
	},

	/**
	 * Check if form is valid (no errors)
	 */
	isValid(errors: ValidationErrors): boolean {
		return Object.keys(errors).length === 0;
	},

	/**
	 * Validate entire form
	 */
	validateForm(
		quantity: number,
		variantId: null | number,
		variants: readonly ProductVariant[],
		productType?: string,
	): ValidationErrors {
		const errors: MutableValidationErrors = {};

		const quantityError = this.validateQuantity(quantity);
		if (quantityError) {
			errors.quantity = quantityError;
		}

		const variantError = this.validateVariant(
			variantId,
			variants,
			productType,
		);
		if (variantError) {
			errors.variant = variantError;
		}

		return errors;
	},
};
